import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { showAlert } from 'actions/app';
import {
  CreateMultipleActivitiesButton,
  CreateMultipleKeywordsButton,
  CreateMultipleNotesButton
} from 'common';
import { CreateMultipleTasksAction, CreationTaskDrawer } from 'features/daily-plan';
import { ORIGIN_TABLE_MAP } from 'features/daily-plan/constants';
import { useTaskDrawerManager } from 'features/daily-plan/hooks';
import {
  CreationSavedSearchDrawer,
  SavedSearchDrawer,
  SearchApplied,
  useSavedSearchDateRestore,
  useSavedSearches
} from 'features/saved-searches';
import { getMatchedStaticDateRange } from 'features/saved-searches/utils';
import { useKeywordDrawerManager, useNoteDrawerManager } from 'hooks';
/** API / EntityRoutes / Endpoints / EntityType */
import useDatatable, {
  addRingCentralContactToStore,
  buildMultiplePhonesActionsRenderer,
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import { useSearchProject } from 'hooks/searchProject';
import { useUrlParamsUpdater } from 'hooks/urlParamsUpdater';
import { useActivityDrawerManager } from 'hooks/useActivityDrawerManager';
import usePageTitle from 'hooks/usePageTitle';
import { useQuickViewPreview } from 'hooks/useQuickViewPreview';
import isEmpty from 'lodash/isEmpty';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { userHasRoles } from 'services/Authorization';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import { ActivityDrawer } from 'UI/components/organisms/ActivityDrawer';
/** Components */
import DataTable from 'UI/components/organisms/DataTable';
import FPQuickView from 'UI/components/organisms/inventoryProfiles/FPQuickView';
import { KeywordsFormDrawer } from 'UI/components/organisms/KeywordsFormDrawer/KeywordsFormDrawer';
import NoteForm from 'UI/components/organisms/NoteForm';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { backNavigateListConfig } from 'UI/constants/config';
import {
  CommunicationActionPreset,
  MULTIPLE_ACTIVITY_CREATION_SECTIONS,
  PageTitles,
  SelectionType
} from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { CandidateEntity, EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { createCustomStaticRanges, getFinalColumns } from 'UI/utils';
import { getMinRequiredFilters } from 'UI/utils/filters';

import {
  /** @typedef {import('react-router-dom').RouteComponentProps} RouterProps */
  /** @typedef {import('react-router-dom'').RouterProps} RouterProps */
  ColumnsDefinitions,
  DateFilters,
  FiltersGroups,
  PeriodColumnBySection,
  VirtualProps
} from './columns';

const pageKey = 'candidates-list';
const dateTypeFilterKey = 'dateTypeFilter';

const orderByOptions = {
  column: 'id',
  direction: 'asc'
};

const numberOfActionColumns = 1;

const MIN_REQUIRED_FILTERS = getMinRequiredFilters();

const getEntityLabel = totalSelected =>
  totalSelected === 1 ? CandidateEntity.singular : CandidateEntity.title;

/**
 * @param {RouterProps} props
 */
const Candidates = props => {
  const { history, location } = props;
  const dispatch = useDispatch();
  const [currentUser] = useUserContext();
  const isHighLevelUser = userHasRoles(
    [
      Roles.Operations,
      Roles.DataCoordinator,
      Roles.Leadership,
      Roles.AssistantRegionalDirector,
      Roles.Coach
    ],
    currentUser
  );

  const multiplePhonesActionButtons = buildMultiplePhonesActionsRenderer({
    customName: 'phoneNumbers',
    handleAction,
    columns: getFinalColumns(ColumnsDefinitions),
    entityType: EntityType.Candidate
  });

  const CANDIDATE_COLUMNS = [
    ...getFinalColumns(ColumnsDefinitions),
    multiplePhonesActionButtons
  ].filter(column => !!column);

  const initialPreferences = getColumnPreferences(
    pageKey,
    0,
    orderByOptions,
    CANDIDATE_COLUMNS,
    numberOfActionColumns,
    location.search
  );

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const apiVersion = checkIfFeatureFlagEnabled(FeatureFlags.CandidatesMultiplePhones) ? 2 : 1;
  const canCreateMultipleTasks = checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanAddTasksFromTable);
  const isSavedSearchesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.CandidatesSavedSearches);
  const isListPersistDateRangeEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.InventoryListPersistDateRange
  );
  const isSavedSearchesDateEnabled = checkIfFeatureFlagEnabled(FeatureFlags.SavedSearchesDate);

  const {
    activeSearch,
    appliedPeriod,
    buildSavedSearchPayload,
    getRestoredDateTypeFilter,
    getSavedSearchButtonDefinitions,
    handleApplySearch: applySearch,
    handleCloseSavedSearchDrawer,
    handleCloseSavedSearchForm,
    handleDeleteSearch,
    handleEditSavedSearchDrawerOpen,
    handleEditSearch,
    handleRemoveSearch,
    isDirty,
    savedDateTypeId,
    savedSearchesUiState,
    savedSearchRestoreKey,
    shouldUseSavedSearchDates
  } = useSavedSearches({
    defaultDateTypeFilterId: isSavedSearchesDateEnabled ? DateFilters[0].id : null,
    isDateEnabled: isSavedSearchesDateEnabled,
    preferencesKey: pageKey
  });

  const {
    activeSection,
    columnPreferences,
    count,
    data,
    filters,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersApply,
    handleFiltersChange,
    handleFiltersToggle,
    handleKeywordChange,
    handleOneFilterChange,
    handlePageChange,
    handlePeriodChange: handleDataTablePeriodChange,
    handlePerPageChange,
    handleSectionChange,
    isBelowMinFiltersThreshold,
    listState,
    mainOptions,
    selectedPeriod,
    queryParams
  } = useDatatable({
    apiVersion,
    columnsDefinitions: CANDIDATE_COLUMNS,
    enableSavedSearches: isSavedSearchesEnabled && savedSearchesUiState.isSearching,
    endpoint: Endpoints.Candidates,
    entityName: 'Candidates',
    entityType: EntityType.Candidate,
    initialPreferences,
    key: pageKey,
    minRequiredFilters: !isHighLevelUser ? MIN_REQUIRED_FILTERS : null,
    numberOfActionColumns,
    orderByOptions,
    periodDefaultValue: shouldUseSavedSearchDates ? appliedPeriod : null,
    periodFilterColumn: PeriodColumnBySection,
    initialSection: isSavedSearchesDateEnabled ? savedDateTypeId : DateFilters[0].id,
    savedSearch: isSavedSearchesEnabled && activeSearch && activeSearch?.search,
    sendDateWithTimezone: true,
    shouldSaveDateRangePreferences: isListPersistDateRangeEnabled && !shouldUseSavedSearchDates,
    shouldScrollOnNavigate: true,
    virtualProps: VirtualProps
  });

  const defaultDateTypeFilter =
    appliedPeriod?.dateTypeFilter ||
    DateFilters.find(item => item.id === activeSection) ||
    DateFilters[0];

  const [dateTypeFilter, setDateTypeFilter] = useState(defaultDateTypeFilter);

  const currentSavedSearchPayload = buildSavedSearchPayload({
    dateTypeFilter,
    filters,
    selectedPeriod
  });

  const restoredDateTypeFilter = getRestoredDateTypeFilter({
    dateFilters: DateFilters,
    fallbackDateFilter: DateFilters[0]
  });

  useEffect(() => {
    handleEditSearch(
      filters,
      shouldUseSavedSearchDates ? selectedPeriod : null,
      shouldUseSavedSearchDates ? dateTypeFilter?.id : null
    );
  }, [dateTypeFilter, filters, handleEditSearch, selectedPeriod, shouldUseSavedSearchDates]);

  const { resetRestoreKey } = useSavedSearchDateRestore({
    activeSection,
    appliedPeriod,
    dateTypeFilter,
    handleDataTablePeriodChange,
    handleSectionChange,
    isSavedSearchesDateEnabled,
    restoredDateTypeFilter,
    savedSearchRestoreKey,
    selectedPeriod,
    setDateTypeFilter
  });

  const handleApplySearch = useCallback(
    params => {
      resetRestoreKey();
      applySearch(params);
    },
    [applySearch, resetRestoreKey]
  );

  const { columns, columnOrder, orderBy, direction } = columnPreferences;
  useUrlParamsUpdater({ history, location, orderBy });

  const { isLoading, showWholeSkeleton, isSideMenuOpen, page, perPage, keyword, userFilter } =
    listState;

  const dataForSelection = useMemo(
    () => data.map(({ id }) => ({ id, type_id: 1, type: 'Candidate' })),
    [data]
  );

  const {
    handleRowSelection,
    selectedIndexesInPage,
    totalSelected,
    filteredItems,
    multiSelectComponents
  } = useMultipleSelection({ data: dataForSelection, count, perPage });

  const {
    isDailyPlanTaskDrawerOpen,
    formattedTaskItems,
    allowedTasks,
    tasksQuery,
    handleCloseTaskDrawer,
    handleOpenTaskDrawer,
    handleOnTaskCreated
  } = useTaskDrawerManager({
    table: ORIGIN_TABLE_MAP.Candidates,
    selectedItems: filteredItems.data,
    selectionType: filteredItems.type,
    queryParams
  });

  const {
    canCreateMultipleNotes,
    formattedSelectedItems,
    handleCloseNoteDrawer,
    handleOpenNoteDrawer,
    isNoteDrawerOpen,
    noteEndpoint
  } = useNoteDrawerManager({
    selectedItems: filteredItems.data,
    selectionType: filteredItems.type,
    table: ORIGIN_TABLE_MAP.Candidates
  });

  const {
    canCreateMultipleKeywords,
    formattedSelectedItems: formattedSelectedKeywordItems,
    handleCloseKeywordDrawer,
    handleOpenKeywordDrawer,
    isKeywordDrawerOpen,
    keywordEndpoint
  } = useKeywordDrawerManager({
    selectedItems: filteredItems.data,
    selectionType: filteredItems.type,
    table: ORIGIN_TABLE_MAP.Candidates
  });

  const {
    activityEndpoint,
    canCreateMultipleActivities,
    formattedSelectedItems: formattedSelectedActivityItems,
    handleCloseActivityDrawer,
    handleOpenActivityDrawer,
    isActivityDrawerOpen
  } = useActivityDrawerManager({
    selectedItems: filteredItems.data,
    selectionType: filteredItems.type,
    table: ORIGIN_TABLE_MAP.Candidates
  });

  const { SearchProjectAction, SearchProjectForms } = useSearchProject({
    filteredItems,
    queryParams
  });

  usePageTitle({ title: PageTitles.Candidate, isLoading });

  function handleAction({ rowData, origin }) {
    addRingCentralContactToStore({
      rowData,
      columns: finalColumns,
      dispatch,
      role: EntityType.Candidate,
      origin
    });
  }

  const { entityId, quickViewState, listViewPreset, quickViewEntity, toggleQuickViewPreview } =
    useQuickViewPreview({ columns, data, entityType: EntityType.Candidate });

  const phoneActionButtons = buildRowActionsRendererDefinition({
    actions: [...CommunicationActionPreset, listViewPreset],
    namespace: EntityType.Candidate,
    handleAction,
    columns: CANDIDATE_COLUMNS
  });

  const handleDateTypeFilterChange = (name, value) => {
    setDateTypeFilter(value);
    handleSectionChange && handleSectionChange(name, value.id);
  };

  const finalColumns = getColumnsToRender([...columns, phoneActionButtons]);

  const handleTabRefresh = async () => {
    await getData();
  };

  const shouldSelectRequiredFilters = isBelowMinFiltersThreshold && (!data || data.length === 0);
  const savedSearchButtonDefinitions = getSavedSearchButtonDefinitions({ filters });

  const handlePeriodChange = useCallback(
    (newPeriod, isFromStaticPeriod) => {
      const period = newPeriod ? { ...newPeriod, isFromStaticPeriod } : null;
      const matchedStaticDateRange = isFromStaticPeriod ? getMatchedStaticDateRange(period) : null;
      handleDataTablePeriodChange(
        newPeriod ? { ...period, staticDateRangeId: matchedStaticDateRange?.id || null } : null
      );
    },
    [handleDataTablePeriodChange]
  );

  const clearSavedSearchPeriod = () => {
    handleDataTablePeriodChange(null);
    handleDateTypeFilterChange(dateTypeFilterKey, DateFilters[0]);
  };

  const handleSavedSearchRemove = () => {
    const onClearPeriod = isSavedSearchesDateEnabled ? clearSavedSearchPeriod : null;
    handleRemoveSearch({ onClearPeriod });
  };

  const handleSavedSearchDelete = params => {
    const onClearPeriod = isSavedSearchesDateEnabled ? clearSavedSearchPeriod : null;
    handleDeleteSearch({ ...params, onClearPeriod });
  };

  return (
    <FiltersLayout
      title="Candidates"
      section={EntityType.Candidate}
      listSelector={
        <AutocompleteSelect
          name="userFilter"
          placeholder="Candidates to show"
          selectedValue={userFilter || mainOptions[0]}
          onSelect={handleOneFilterChange}
          defaultOptions={mainOptions}
          disableClearable
        />
      }
      defaultRange={selectedPeriod || null}
      extraSelector={
        <AutocompleteSelect
          name={dateTypeFilterKey}
          placeholder="Filter By"
          selectedValue={dateTypeFilter}
          onSelect={handleDateTypeFilterChange}
          defaultOptions={DateFilters}
          disableClearable
        />
      }
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      filters={filters}
      isSideMenuOpen={isSideMenuOpen}
      isLoading={showWholeSkeleton}
      isPeriodClearable
      groups={FiltersGroups}
      enableStore={false}
      onSearch={handleFiltersApply}
      onFiltersChange={handleFiltersChange}
      onMenuToggle={handleFiltersToggle}
      titleLabelProps={backNavigateListConfig}
      onPeriodChange={handlePeriodChange}
      minRequiredFilters={shouldSelectRequiredFilters ? MIN_REQUIRED_FILTERS : null}
      shouldSelectRequiredFilters={shouldSelectRequiredFilters}
      enableSavedSearches={isSavedSearchesEnabled}
      savedSearchesHeader={
        isSavedSearchesEnabled &&
        activeSearch && (
          <SearchApplied
            isDirty={isDirty}
            onRemoveSearch={handleSavedSearchRemove}
            onSaveSearch={!isEmpty(filters) ? handleEditSavedSearchDrawerOpen : null}
            searchName={activeSearch?.name}
            searchOwner={activeSearch?.owner}
          />
        )
      }
      savedSearchesActions={
        isSavedSearchesEnabled && (
          <FPButtonBuilder
            GridContainerProps={{ justifyContent: 'flex-end', spacing: 2 }}
            buttonDefinitions={savedSearchButtonDefinitions}
          />
        )
      }
    >
      <DataTable
        isExpandable
        loading={showWholeSkeleton}
        refreshing={isLoading}
        data={data}
        columns={finalColumns}
        count={count}
        filter={false}
        page={page}
        rowsPerPage={perPage}
        searchText={keyword}
        sortOrder={{ name: orderBy, direction }}
        columnOrder={columnOrder?.length ? columnOrder : undefined}
        draggableColumns={{
          enabled: true
        }}
        enableCustomFilters
        onToggleFilters={handleFiltersToggle}
        onSearchTextChange={handleKeywordChange}
        onColumnSortChange={handleColumnSortChange}
        onPerPageClick={handlePerPageChange}
        onPageClick={handlePageChange}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onRowSelectionChange={handleRowSelection}
        rowsSelected={selectedIndexesInPage}
        selectToolbarPlacement="none"
        CustomToolbar={
          <CustomTableToolbarSelect total={totalSelected}>
            <When
              condition={canCreateMultipleNotes && filteredItems.type === SelectionType.Include}
            >
              <CreateMultipleNotesButton onClick={handleOpenNoteDrawer} />
            </When>
            <When
              condition={
                canCreateMultipleActivities && filteredItems.type === SelectionType.Include
              }
            >
              <CreateMultipleActivitiesButton onClick={handleOpenActivityDrawer} />
            </When>
            <When
              condition={canCreateMultipleKeywords && filteredItems.type === SelectionType.Include}
            >
              <CreateMultipleKeywordsButton onClick={handleOpenKeywordDrawer} />
            </When>
            <When condition={canCreateMultipleTasks}>
              <CreateMultipleTasksAction onClick={handleOpenTaskDrawer} />
            </When>
            <SearchProjectAction />
          </CustomTableToolbarSelect>
        }
        components={multiSelectComponents}
      />
      <SearchProjectForms />
      <When condition={entityId && quickViewState.isQuickViewOpen}>
        <FPQuickView
          drawerProps={{
            open: quickViewState.isQuickViewOpen
          }}
          entityType={quickViewEntity}
          onClose={toggleQuickViewPreview}
          itemsToNavigate={data}
          id={entityId}
          onEditionCompleted={handleTabRefresh}
        />
      </When>
      <When
        condition={isDailyPlanTaskDrawerOpen && (formattedTaskItems.length > 0 || !!tasksQuery)}
      >
        <CreationTaskDrawer
          onClose={handleCloseTaskDrawer}
          listItems={formattedTaskItems}
          allowedTaskTypes={allowedTasks}
          tasksQuery={tasksQuery}
          totalSelected={totalSelected}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>
      <When condition={isNoteDrawerOpen}>
        <NoteForm
          endpoint={noteEndpoint}
          extraPayloadData={{ candidates: formattedSelectedItems }}
          FPDrawerProps={{ open: isNoteDrawerOpen }}
          onNoteClosed={handleCloseNoteDrawer}
          topContent={
            <>
              <Typography variant="subtitle2" component="h6" gutterBottom>
                {strings.formatString(
                  strings.inventory.shared.listing.multipleActivityCreation.total,
                  {
                    activity: MULTIPLE_ACTIVITY_CREATION_SECTIONS.notes,
                    entity: getEntityLabel(totalSelected),
                    total: totalSelected
                  }
                )}
              </Typography>
              <Divider />
            </>
          }
        />
      </When>

      <When condition={isKeywordDrawerOpen}>
        <KeywordsFormDrawer
          endpoint={keywordEndpoint}
          entityType={EntityType.Candidate}
          selectedItems={formattedSelectedKeywordItems}
          FPDrawerProps={{ open: isKeywordDrawerOpen }}
          onClose={handleCloseKeywordDrawer}
        />
      </When>

      <When condition={isActivityDrawerOpen}>
        <ActivityDrawer
          extraPayloadData={{ candidates: formattedSelectedActivityItems }}
          onClose={handleCloseActivityDrawer}
          open={isActivityDrawerOpen}
          submitEndpoint={activityEndpoint}
          topContent={
            <>
              <Typography variant="subtitle2" component="h6" gutterBottom>
                {strings.formatString(
                  strings.inventory.shared.listing.multipleActivityCreation.total,
                  {
                    activity: MULTIPLE_ACTIVITY_CREATION_SECTIONS.activities,
                    entity: getEntityLabel(totalSelected),
                    total: totalSelected
                  }
                )}
              </Typography>
              <Divider />
            </>
          }
        />
      </When>

      <When condition={isSavedSearchesEnabled && savedSearchesUiState.isSavedSearchDrawerOpen}>
        <SavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Candidate}
          onApplySearch={handleApplySearch}
          onClose={handleCloseSavedSearchDrawer}
          onDeleteSearch={handleSavedSearchDelete}
          open={savedSearchesUiState.isSavedSearchDrawerOpen}
        />
      </When>

      <When
        condition={
          isSavedSearchesEnabled &&
          (savedSearchesUiState.isCreationSavedSearchDrawerOpen ||
            savedSearchesUiState.isEditSavedSearchDrawerOpen)
        }
      >
        <CreationSavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Candidate}
          onApplySearch={handleApplySearch}
          onSaveCompleted={handleCloseSavedSearchForm}
          onClose={handleCloseSavedSearchForm}
          open
          savedSearchId={savedSearchesUiState.isEditSavedSearchDrawerOpen ? activeSearch?.id : null}
          search={currentSavedSearchPayload}
        />
      </When>
    </FiltersLayout>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    onShowAlert: alert => dispatch(showAlert(alert))
  };
};

export default connect(null, mapDispatchToProps)(Candidates);
